import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, Pencil, Trash2, KeyRound, Users } from "lucide-react";
import { toast } from "sonner";

function PasswordField({ value, onChange, id }) {
  return <Input id={id} type="text" required minLength={6} value={value} onChange={onChange} data-testid={id} className="font-mono" />;
}

export default function AdminDietitians() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", default_password: "" });
  const [editForm, setEditForm] = useState({ user_id: "", name: "", default_password: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/dietitians").then(({ data }) => setList(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/dietitians", form);
      toast.success(`Dietitian ${form.name} added. Share the default password — they can change it on first login.`);
      setForm({ name: "", email: "", default_password: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const startEdit = (d) => { setEditForm({ user_id: d.user_id, name: d.name, default_password: "" }); setEditOpen(true); };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: editForm.name };
      if (editForm.default_password) payload.default_password = editForm.default_password;
      await api.put(`/admin/dietitians/${editForm.user_id}`, payload);
      toast.success("Dietitian updated");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!confirm(`Delete ${d.name} and ALL their patients, plans, recipes? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/dietitians/${d.user_id}`);
      toast.success("Dietitian deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const randomPass = () => Math.random().toString(36).slice(2, 10);

  return (
    <div className="space-y-6" data-testid="admin-dietitians-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Super admin</p>
          <h1 className="text-3xl font-bold mt-1">Dietitians</h1>
          <p className="text-muted-foreground mt-1">Provision dietitian accounts. They sign in via the dietitian portal and can change their password themselves.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-full" data-testid="add-dietitian-button"><UserPlus size={16} /> Add dietitian</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-dietitian-dialog">
            <DialogHeader><DialogTitle>Add dietitian</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-dietitian-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-dietitian-email" />
                <p className="text-xs text-muted-foreground">The dietitian cannot change this themselves later.</p>
              </div>
              <div className="space-y-2">
                <Label>Default password</Label>
                <div className="flex gap-2">
                  <PasswordField id="new-dietitian-password" value={form.default_password} onChange={(e) => setForm({ ...form, default_password: e.target.value })} />
                  <Button type="button" variant="outline" onClick={() => setForm({ ...form, default_password: randomPass() })} data-testid="generate-password">Generate</Button>
                </div>
                <p className="text-xs text-muted-foreground">Share this password — the dietitian can change it from their profile.</p>
              </div>
              <Button type="submit" disabled={saving} className="w-full" data-testid="create-dietitian-submit">{saving ? "Creating…" : "Create dietitian"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="nt-card overflow-hidden">
        {loading ? <div className="p-8 text-muted-foreground">Loading…</div> :
          list.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="no-dietitians">
              <Users size={32} className="mx-auto mb-3 text-primary" />
              <p className="font-semibold">No dietitians yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {list.map((d) => (
                <div key={d.user_id} className="px-6 py-4 flex items-center justify-between gap-3" data-testid={`dietitian-row-${d.user_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-semibold">
                      {d.name?.[0]?.toUpperCase() || "D"}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{d.name}</div>
                      <div className="text-xs text-muted-foreground">{d.email} • {d.patient_count} patients</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => startEdit(d)} className="gap-1" data-testid={`edit-dietitian-${d.user_id}`}><Pencil size={14} /> Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => remove(d)} className="gap-1 text-destructive border-destructive/40 hover:bg-destructive/10" data-testid={`delete-dietitian-${d.user_id}`}><Trash2 size={14} /> Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-dietitian-dialog">
          <DialogHeader><DialogTitle>Edit dietitian</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-dietitian-name" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><KeyRound size={14} /> Reset password (optional)</Label>
              <div className="flex gap-2">
                <Input type="text" minLength={6} placeholder="Leave blank to keep current" value={editForm.default_password} onChange={(e) => setEditForm({ ...editForm, default_password: e.target.value })} className="font-mono" data-testid="edit-dietitian-password" />
                <Button type="button" variant="outline" onClick={() => setEditForm({ ...editForm, default_password: randomPass() })}>Generate</Button>
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full" data-testid="save-dietitian-button">{saving ? "Saving…" : "Save"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
